import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDataSource } from '@/src/renderer/lib/dataSource'
import type { LifeSpace } from '@/src/renderer/lib/db/models'
import { MOVE_LIFE_SPACES, projectRows, shownSections, type MoveTargetProject, type MoveTargets } from '../utils/moveTargets'

/**
 * Alle Ziele von **Verschieben** auf einmal, sobald das Panel aufgeht (#257, #263): die Projekte je
 * Lebensbereich samt Abschnitten. Die Suche geht über alle Lebensbereiche, deshalb liegt alles
 * schon da. Die Abschnitte ohne Live-Abfrage — das Panel lebt Sekunden.
 *
 * Ein Projekt ohne Namen heißt hier wie in der Seitenleiste („Neues Projekt“) — sonst stünde eine leere
 * Zeile da, und die Meldung sagte „Nach  verschoben“ (gemeldet 05.10.2026).
 *
 * `null`, solange geladen wird.
 */
export function useMoveDestinations(): MoveTargets | null {
  const { t } = useTranslation()
  const source = useDataSource()
  const projects = source.useProjects()
  const untitled = t('project.defaultName')
  const rows = useMemo(() => {
    if (!projects) return null
    const named = projects.map(project => (project.name.trim() ? project : { ...project, name: untitled }))
    return MOVE_LIFE_SPACES.map(lifeSpace => [lifeSpace, projectRows(named.filter(project => project.lifeSpace === lifeSpace))] as const)
  }, [projects, untitled])
  const [targets, setTargets] = useState<MoveTargets | null>(null)
  useEffect(() => {
    if (!rows) return
    let alive = true
    const all = rows.flatMap(([, list]) => list)
    void Promise.all(all.map(({ project }) => source.getSectionsByProject(project.id))).then(lists => {
      if (!alive) return
      const byProject = new Map(all.map(({ project }, i) => [project.id, shownSections(lists[i] ?? [])]))
      setTargets(new Map<LifeSpace, MoveTargetProject[]>(rows.map(([lifeSpace, list]) => [lifeSpace, list.map(row => ({ ...row, sections: byProject.get(row.project.id) ?? [] }))])))
    })
    return () => { alive = false }
  }, [rows, source])
  return targets
}
